import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { loucaApi, buildImageUrl } from '../services/loucaApi';
import { useAuth } from '../contexts/AuthContext';
import ImageModal from '../components/ImageModal';
import { normalizeAnalysisWorkflow } from '../utils/analysisWorkflow';

const ensureArray = (value) => (Array.isArray(value) ? value : []);
const TEST_HISTORY_KEY = 'louca_image_test_history';
const LAST_IMAGE_KEY = 'louca_last_test_image';

const ImageTestPage = () => {
  const { user } = useAuth();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState('yolo_1.0.0');
  const [yoloParams, setYoloParams] = useState({
    confidence_thresh: 0.25,
    iou_threshold: 0.45,
    max_det: 300,
    batch_size: 8,
    imgsz: 1024,
  });
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [lastImageUrl, setLastImageUrl] = useState(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);

  const workflows = [
    { id: 'yolo_1.0.0', label: 'YOLO 1.0.0 (Default)' },
    { id: 'cars2025_yolo_v1', label: 'YOLO 2025 (Cars2025)' },
    { id: 'llm_images_v0.1', label: 'LLM Complex (Gemini)' },
    { id: 'images_to_ia_v1', label: 'LLaVA Local' },
  ];

  // Load test history and last image on mount
  useEffect(() => {
    try {
      const history = localStorage.getItem(TEST_HISTORY_KEY);
      if (history) {
        setTestHistory(JSON.parse(history));
      }
      const lastImage = localStorage.getItem(LAST_IMAGE_KEY);
      if (lastImage) {
        setLastImageUrl(lastImage);
      }
    } catch (e) {
      console.warn('Failed to load test history:', e);
    }
  }, []);

  const saveTestToHistory = (testData, fullResult) => {
    try {
      const history = JSON.parse(localStorage.getItem(TEST_HISTORY_KEY) || '[]');
      const newEntry = {
        ...testData,
        full_result: fullResult, // Save complete result for later viewing
        timestamp: new Date().toISOString(),
        id: Date.now().toString(),
      };
      history.unshift(newEntry); // Add to beginning
      // Keep only last 20 tests
      const limited = history.slice(0, 20);
      localStorage.setItem(TEST_HISTORY_KEY, JSON.stringify(limited));
      setTestHistory(limited);
    } catch (e) {
      console.warn('Failed to save test history:', e);
    }
  };

  const handleLoadHistoryEntry = (entry) => {
    if (entry.full_result) {
      setResult(entry.full_result);
      setSelectedWorkflow(entry.workflow);
      setSelectedHistoryId(entry.id);
      setError(null);
      setShowDebug(false);
      // Scroll to results
      setTimeout(() => {
        document.querySelector('[data-results-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setError(null);
      setResult(null);
      // Save image URL for reuse
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = event.target.result;
        setLastImageUrl(imageUrl);
        localStorage.setItem(LAST_IMAGE_KEY, imageUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUseLastImage = () => {
    if (lastImageUrl) {
      // Convert data URL back to File
      fetch(lastImageUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'last_test_image.jpg', { type: 'image/jpeg' });
          setSelectedImage(file);
        })
        .catch(e => {
          console.error('Failed to load last image:', e);
          setError('Impossible de charger la dernière image');
        });
    }
  };

  const handleWorkflowChange = (e) => {
    setSelectedWorkflow(e.target.value);
    setResult(null);
    setError(null);
  };

  const handleYoloParamChange = (param, value) => {
    setYoloParams((prev) => ({
      ...prev,
      [param]: parseFloat(value) || 0,
    }));
  };

  const handleTest = async () => {
    if (!selectedImage) {
      setError('Veuillez sélectionner une image');
      return;
    }

    setTesting(true);
    setError(null);
    setResult(null);

    try {
      const owner = { type: 'driver', id: user?.user_id || 'DEMO_DRIVER' };
      const yoloParamsToSend = ['yolo_1.0.0', 'cars2025_yolo_v1'].includes(selectedWorkflow)
        ? yoloParams
        : {};

      const response = await loucaApi.testImage(
        selectedImage,
        selectedWorkflow,
        user?.user_id || 'DEMO_DRIVER',
        owner,
        yoloParamsToSend
      );

      if (response.status === 'success') {
        setResult(response.data);
        setError(null); // Clear any previous errors
        setSelectedHistoryId(null); // Clear history selection
        // Save to history with full result
        saveTestToHistory({
          workflow: selectedWorkflow,
          inspection_id: response.data.inspection_id,
          image_name: selectedImage.name,
          image_size: selectedImage.size,
          yolo_params: yoloParamsToSend,
          damages_count: response.data.analysis_workflow?.damages?.length || 0,
          timestamp: response.timestamp,
        }, response.data);
      } else if (response.status === 'error') {
        // Backend returned error status (e.g., LLM error but YOLO succeeded)
        // Still show YOLO results if available
        if (response.data && response.data.analysis_workflow) {
          setResult(response.data);
          // Show error message prominently
          const errorInfo = response.data.error || {};
          setError(
            `❌ ${response.message || 'Erreur lors du traitement'}\n\n` +
            `Type: ${errorInfo.error_type || 'Unknown'}\n` +
            (errorInfo.error ? `Erreur: ${errorInfo.error}\n` : '') +
            (errorInfo.traceback ? `\nTraceback:\n${errorInfo.traceback}` : '')
          );
        } else {
          setError(response.message || response.detail || 'Erreur lors du test');
          setResult(null);
        }
        setSelectedHistoryId(null);
      } else {
        // Backend returned unknown status
        setError(response.message || response.detail || 'Erreur lors du test');
        setResult(null);
      }
    } catch (err) {
      console.error('Test error:', err);
      const errorDetail = err.response?.data?.detail;
      let errorMessage = err.message || 'Erreur lors du test';
      
      // Try to parse JSON error detail
      if (typeof errorDetail === 'string') {
        try {
          const parsed = JSON.parse(errorDetail);
          errorMessage = `${parsed.error || errorMessage}\n\n` +
            `Type: ${parsed.error_type || 'Unknown'}\n` +
            (parsed.traceback ? `\nTraceback:\n${parsed.traceback}` : '');
        } catch {
          // Not JSON, use as-is
          errorMessage = errorDetail || errorMessage;
        }
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        errorMessage = `${errorDetail.error || errorMessage}\n\n` +
          `Type: ${errorDetail.error_type || 'Unknown'}\n` +
          (errorDetail.traceback ? `\nTraceback:\n${errorDetail.traceback}` : '');
      } else if (errorDetail) {
        errorMessage = errorDetail;
      }
      
      setError(errorMessage);
    } finally {
      setTesting(false);
    }
  };

  const workflow = result?.analysis_workflow ? normalizeAnalysisWorkflow(result.analysis_workflow) : null;
  const damages = workflow?.damages || [];
  const media = workflow?.media || {};
  // Debug can be in result.debug (from API response.data) or result.data.debug
  const debug = result?.debug || result?.data?.debug || {};

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">Test unitaire - Analyse d'image</h1>

        {/* Upload Section */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">Configuration du test</h2>

          {/* Image Selection */}
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-2">
              <label className="block text-white font-semibold">Image à analyser</label>
              {lastImageUrl && (
                <button
                  onClick={handleUseLastImage}
                  className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                >
                  Réutiliser dernière image
                </button>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {selectedImage && (
              <div className="mt-2 text-sm text-slate-300">
                Image sélectionnée: {selectedImage.name} ({(selectedImage.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>

          {/* Workflow Selection */}
          <div className="mb-6">
            <label className="block text-white font-semibold mb-2">Workflow</label>
            <select
              value={selectedWorkflow}
              onChange={handleWorkflowChange}
              disabled={testing}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              {workflows.map((wf) => (
                <option key={wf.id} value={wf.id}>
                  {wf.label}
                </option>
              ))}
            </select>
          </div>

          {/* YOLO Hyperparameters (only for YOLO workflows) */}
          {['yolo_1.0.0', 'cars2025_yolo_v1'].includes(selectedWorkflow) && (
            <div className="mb-6 bg-slate-700 border border-slate-600 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Hyperparamètres YOLO</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">
                    Confidence Threshold (0.0-1.0)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={yoloParams.confidence_thresh}
                    onChange={(e) => handleYoloParamChange('confidence_thresh', e.target.value)}
                    disabled={testing}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">
                    IOU Threshold (0.0-1.0)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={yoloParams.iou_threshold}
                    onChange={(e) => handleYoloParamChange('iou_threshold', e.target.value)}
                    disabled={testing}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Max Detections</label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={yoloParams.max_det}
                    onChange={(e) => handleYoloParamChange('max_det', e.target.value)}
                    disabled={testing}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Batch Size</label>
                  <input
                    type="number"
                    min="1"
                    max="32"
                    value={yoloParams.batch_size}
                    onChange={(e) => handleYoloParamChange('batch_size', e.target.value)}
                    disabled={testing}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Image Size (imgsz)</label>
                  <input
                    type="number"
                    min="128"
                    max="2048"
                    step="32"
                    value={yoloParams.imgsz}
                    onChange={(e) => handleYoloParamChange('imgsz', e.target.value)}
                    disabled={testing}
                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Test Button */}
          <button
            onClick={handleTest}
            disabled={!selectedImage || testing}
            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            {testing ? 'Analyse en cours...' : 'Lancer le test'}
          </button>

          {error && (
            <div className="mt-4 p-4 bg-red-900 border border-red-700 rounded-lg">
              <div className="text-red-200 whitespace-pre-wrap font-mono text-sm">{error}</div>
            </div>
          )}
        </div>

        {/* Test History */}
        {testHistory.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">Historique des tests ({testHistory.length})</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {testHistory.map((entry) => (
                <button
                  key={entry.id}
                  onClick={() => handleLoadHistoryEntry(entry)}
                  className={`w-full text-left bg-slate-700 border rounded p-3 text-sm transition-colors ${
                    selectedHistoryId === entry.id
                      ? 'border-blue-500 bg-slate-600'
                      : 'border-slate-600 hover:border-slate-500 hover:bg-slate-600'
                  }`}
                >
                  <div className="text-white font-semibold">{entry.workflow}</div>
                  <div className="text-slate-300">
                    {new Date(entry.timestamp).toLocaleString('fr-FR')} • {entry.damages_count} dommages • {entry.inspection_id}
                  </div>
                  {entry.full_result && (
                    <div className="text-xs text-slate-400 mt-1">Cliquez pour voir les détails</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && workflow && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6" data-results-section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Résultats</h2>
              {debug && Object.keys(debug).length > 0 && (
                <button
                  onClick={() => setShowDebug(!showDebug)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                >
                  {showDebug ? 'Masquer' : 'Afficher'} détails IA
                </button>
              )}
            </div>

            {/* Workflow Info */}
            <div className="mb-6 p-4 bg-slate-700 rounded-lg">
              <div className="text-sm text-slate-300">
                <div>Workflow: <span className="text-white font-semibold">{selectedWorkflow}</span></div>
                <div>Inspection ID: <span className="text-white font-semibold">{result.inspection_id}</span></div>
                {result.analysis_workflow?.metadata?.hyperparameters && (
                  <div className="mt-2">
                    <div className="text-white font-semibold mb-1">Hyperparamètres utilisés:</div>
                    <div className="text-slate-300 text-xs">
                      Confidence: {result.analysis_workflow.metadata.hyperparameters.confidence_thresh} | 
                      IOU: {result.analysis_workflow.metadata.hyperparameters.iou_threshold} | 
                      Max Det: {result.analysis_workflow.metadata.hyperparameters.max_det} | 
                      Batch Size: {result.analysis_workflow.metadata.hyperparameters.batch_size} | 
                      Image Size: {result.analysis_workflow.metadata.hyperparameters.imgsz || 1024}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Debug Information (YOLO raw + LLM prompt/response) */}
            {showDebug && debug && (
              <div className="mb-6 space-y-4">
                {/* YOLO Raw Detections */}
                {debug.yolo_raw_detections && debug.yolo_raw_detections.length > 0 && (
                  <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Détections YOLO brutes ({debug.yolo_summary?.total_detections || 0})
                    </h3>
                    <div className="text-sm text-slate-300 font-mono max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">
                        {JSON.stringify(debug.yolo_raw_detections, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* LLM Prompt */}
                {debug.llm_debug?.prompt && (
                  <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-2">Prompt LLM</h3>
                    <div className="text-sm text-slate-300 font-mono max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">{debug.llm_debug.prompt}</pre>
                    </div>
                  </div>
                )}

                {/* LLM Response */}
                {debug.llm_debug?.response && (
                  <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-2">Réponse LLM</h3>
                    <div className="text-sm text-slate-300 font-mono max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">{debug.llm_debug.response}</pre>
                    </div>
                  </div>
                )}

                {/* LLM Raw Response */}
                {debug.llm_debug?.response_raw && (
                  <div className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-2">Réponse LLM (brute)</h3>
                    <div className="text-sm text-slate-300 font-mono max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">{debug.llm_debug.response_raw}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Damages */}
            {damages.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Dommages détectés ({damages.length})
                </h3>
                <div className="space-y-4">
                  {damages.map((damage, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-700 border border-slate-600 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-white font-semibold">
                            {damage.part || damage.part_id || 'Partie non identifiée'}
                          </div>
                          <div className="text-slate-300 text-sm mt-1">
                            Type: {damage.type || 'inconnu'} | 
                            Sévérité: {damage.severity || 'inconnue'} | 
                            Confiance: {damage.confidence ? (damage.confidence * 100).toFixed(1) + '%' : 'N/A'}
                          </div>
                          {damage.estimated_cost && (
                            <div className="text-slate-300 text-sm mt-1">
                              Coût estimé: {damage.estimated_cost.total || 0}€
                            </div>
                          )}
                        </div>
                        {damage.outlined_image_url && (
                          <img
                            src={buildImageUrl(damage.outlined_image_url)}
                            alt={`Dommage ${idx + 1}`}
                            className="w-32 h-32 object-cover rounded cursor-pointer ml-4"
                            onClick={() => setActiveImage(buildImageUrl(damage.outlined_image_url))}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Outlined Images from media.outlined */}
            {ensureArray(media.outlined).length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Images avec détourages ({ensureArray(media.outlined).length})
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  {ensureArray(media.outlined).map((img, idx) => (
                    <img
                      key={idx}
                      src={buildImageUrl(img.url || img.path)}
                      alt={`Détourage ${idx + 1}`}
                      className="w-full h-32 object-cover rounded cursor-pointer"
                      onClick={() => setActiveImage(buildImageUrl(img.url || img.path))}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Outlined Images from debug.outlined_results */}
            {debug?.outlined_results?.outlined_images && debug.outlined_results.outlined_images.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Toutes les images détourées YOLO ({debug.outlined_results.outlined_images.length})
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  {debug.outlined_results.outlined_images.map((img, idx) => {
                    const imgUrl = img.url || img.path;
                    const fullUrl = imgUrl ? (imgUrl.startsWith('http') ? imgUrl : buildImageUrl(imgUrl)) : null;
                    return fullUrl ? (
                      <div key={idx} className="relative group">
                        <img
                          src={fullUrl}
                          alt={`Détourage YOLO ${idx + 1}`}
                          className="w-full h-32 object-cover rounded cursor-pointer border-2 border-slate-600 hover:border-blue-500"
                          onClick={() => setActiveImage(fullUrl)}
                          onError={(e) => {
                            console.error('Erreur chargement image:', fullUrl, img);
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 rounded-b">
                          {img.detection?.class || 'unknown'} ({(img.detection?.confidence || 0).toFixed(3)})
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {damages.length === 0 && ensureArray(media.outlined).length === 0 && (
              <div className="text-slate-300 text-center py-8">
                Aucun dommage détecté
                {debug.yolo_summary?.total_detections > 0 && (
                  <div className="mt-2 text-sm text-orange-400">
                    ⚠️ YOLO a détecté {debug.yolo_summary.total_detections} objets mais le LLM n'a pas généré de dommages
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Image Modal */}
        {activeImage && (
          <ImageModal
            imageUrl={activeImage}
            onClose={() => setActiveImage(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default ImageTestPage;
